import React from 'react';
import { useForm } from 'react-hook-form';
import { Category } from '../../types';
import Input from './Input';
import Select from './Select';
import TextArea from './TextArea';
import Button from './Button';

interface CategoryFormProps {
  category?: Category;
  onSubmit: (data: Partial<Category>) => void;
  onCancel: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ category, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<Category>({
    defaultValues: category || {
      name: '',
      type: 'expense',
      description: '',
      isActive: true
    }
  });

  const handleFormSubmit = (data: Category) => {
    onSubmit({
      ...data,
      ...(category ? { id: category.id, documentId: category.documentId } : {}),
      createdAt: category?.createdAt || new Date().toISOString()
    });
  };

  const typeOptions = [
    { value: 'expense', label: 'Expense' },
    { value: 'income', label: 'Income' },
    { value: 'asset', label: 'Asset' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {category ? 'Edit Category' : 'Add New Category'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Category Name"
            {...register('name', { 
              required: 'Category name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' }
            })}
            error={errors.name?.message}
            placeholder="Enter category name"
          />

          <Select
            label="Category Type"
            {...register('type', { required: 'Category type is required' })}
            options={typeOptions}
            error={errors.type?.message}
          />
        </div>

        <TextArea
          label="Description"
          {...register('description', {
            maxLength: { value: 500, message: 'Description must be less than 500 characters' }
          })}
          error={errors.description?.message}
          placeholder="Enter category description"
          rows={3}
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            {...register('isActive')}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
            Active Category
          </label>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
          >
            {category ? 'Update Category' : 'Create Category'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;